import { formatCompactValue } from "@/lib/format";
import type { WorkspaceField } from "@/lib/mlx-workspace-client";
import { percentDisplayValue } from "./record-amounts";

export type ChartPoint = {
  label: string;
  value: number;
};

export type MetricDisplayKind =
  | "number"
  | "currency"
  | "percent_fraction"
  | "percent_points"
  | { type: "currency"; currencyCode: string };

// Matches DASHBOARD_PALETTE in record-dashboard-shell.tsx: five identity hues
// plus one muted overflow slot. Series never cycle back into the hues.
export const DASHBOARD_MAX_SERIES = 6;

export function additiveAggregate(aggregate: string): boolean {
  return aggregate === "sum" || aggregate === "count";
}

export function foldChartPoints(
  points: ChartPoint[],
  maxSeries = DASHBOARD_MAX_SERIES,
): ChartPoint[] {
  if (points.length <= maxSeries) return points;
  const kept = new Set(
    [...points].sort((left, right) => right.value - left.value).slice(0, maxSeries - 1),
  );
  const tail = points.filter((point) => !kept.has(point));
  return [
    ...points.filter((point) => kept.has(point)),
    { label: `Other (${tail.length})`, value: tail.reduce((total, point) => total + point.value, 0) },
  ];
}

export function isCurrencyMetric(field: WorkspaceField | null): boolean {
  // Currency display comes from the field schema alone; guessing from
  // key/label hints mislabelled mixed-currency number fields as GBP.
  return field?.type === "currency";
}

export function metricDisplayKind(field: WorkspaceField | null): MetricDisplayKind {
  if (field?.type === "percent") {
    return field.config?.percentScale === "fraction" ? "percent_fraction" : "percent_points";
  }
  return isCurrencyMetric(field)
    ? {
        type: "currency",
        currencyCode:
          typeof field?.config?.currency === "string"
            ? field.config.currency
            : typeof field?.config?.symbol === "string"
              ? field.config.symbol
              : "GBP",
      }
    : "number";
}

// Counts are plain numbers even when the widget names a currency/percent field.
export function widgetDisplayKind(aggregate: string, field: WorkspaceField | null): MetricDisplayKind {
  return aggregate === "count" ? "number" : metricDisplayKind(field);
}

export function compactNumber(value: number, kind: MetricDisplayKind | boolean): string {
  if (!Number.isFinite(value)) return "-";
  const displayKind = typeof kind === "boolean" ? (kind ? "currency" : "number") : kind;
  if (displayKind === "percent_fraction" || displayKind === "percent_points") {
    const scale = displayKind === "percent_points" ? "points" : "fraction";
    return `${percentDisplayValue(value, scale).toLocaleString("en-GB", { maximumFractionDigits: 1 })}%`;
  }
  return formatCompactValue(value, { currency: currencyCode(displayKind) });
}

export function isCurrencyDisplayKind(kind: MetricDisplayKind): boolean {
  return currencyCode(kind) !== null;
}

// The unit a chart names once in its widget header (ticks stay bare).
export function displayUnitLabel(kind: MetricDisplayKind): string | undefined {
  return currencyCode(kind) ?? undefined;
}

function currencyCode(kind: MetricDisplayKind): string | null {
  if (kind === "currency") return "GBP";
  return typeof kind === "object" ? kind.currencyCode : null;
}

// Relative change against the prior period; null when undefined (no prior
// window, zero baseline, or non-finite inputs).
export function metricPeriodDelta(
  value: number,
  previous: number | undefined,
): number | null {
  if (previous === undefined || !Number.isFinite(previous) || previous === 0) return null;
  if (!Number.isFinite(value)) return null;
  return (value - previous) / Math.abs(previous);
}
